import React from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useTenantTerm } from '../../hooks/useTenantTerm';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Tenant, TenantInput } from '../../types/tenants';
import { TenantKind } from '../../types/Enums';
import { translate } from '../../types/EnumTranslations';

interface TenantModalProps {
    show: boolean;
    onHide: () => void;
    onSubmit: (input: TenantInput) => Promise<void>;
    /** When given, the modal edits this tenant; otherwise it creates one. */
    tenant?: Tenant;
    submitting?: boolean;
}

interface TenantFormValues {
    kind: TenantKind;
    name: string;
    orgNumber: string;
    email: string;
    phone: string;
    street: string;
    postalCode: string;
    city: string;
    comment: string;
}

const schema = Yup.object({
    kind: Yup.mixed<TenantKind>().oneOf(Object.values(TenantKind)).required(),
    name: Yup.string().trim().required('Namn krävs').max(200, 'Max 200 tecken'),
    orgNumber: Yup.string().trim().max(20, 'Max 20 tecken'),
    email: Yup.string().trim().email('Ogiltig e-postadress').max(200, 'Max 200 tecken'),
    phone: Yup.string().trim().max(50, 'Max 50 tecken'),
    street: Yup.string().trim().max(200, 'Max 200 tecken'),
    postalCode: Yup.string().trim().max(20, 'Max 20 tecken'),
    city: Yup.string().trim().max(100, 'Max 100 tecken'),
    comment: Yup.string().trim().max(500, 'Max 500 tecken'),
});

const toValues = (tenant?: Tenant): TenantFormValues => ({
    kind: tenant?.kind ?? TenantKind.Person,
    name: tenant?.name ?? '',
    orgNumber: tenant?.orgNumber ?? '',
    email: tenant?.email ?? '',
    phone: tenant?.phone ?? '',
    street: tenant?.street ?? '',
    postalCode: tenant?.postalCode ?? '',
    city: tenant?.city ?? '',
    comment: tenant?.comment ?? '',
});

const toInput = (values: TenantFormValues): TenantInput => ({
    kind: values.kind,
    name: values.name.trim(),
    orgNumber: values.kind === TenantKind.Company ? values.orgNumber.trim() || undefined : undefined,
    email: values.email.trim() || undefined,
    phone: values.phone.trim() || undefined,
    street: values.street.trim() || undefined,
    postalCode: values.postalCode.trim() || undefined,
    city: values.city.trim() || undefined,
    comment: values.comment.trim() || undefined,
});

interface TenantFormProps {
    onSubmit: (input: TenantInput) => Promise<void>;
    onCancel: () => void;
    /** When given, the form edits this tenant; otherwise it creates one. */
    tenant?: Tenant;
    submitting?: boolean;
}

/**
 * The tenant form (body + Avbryt/Spara footer) without its modal, so it can be
 * a step inside another modal, e.g. TenantPickerModal's "Ny hyresgäst".
 * Formik + Yup per web/CLAUDE.md (first form on that stack).
 */
export const TenantForm: React.FC<TenantFormProps> = ({ onSubmit, onCancel, tenant, submitting = false }) => {
    const isEditing = !!tenant;

    const handleSubmit = async (values: TenantFormValues, helpers: FormikHelpers<TenantFormValues>) => {
        try {
            await onSubmit(toInput(values));
        } catch (err) {
            helpers.setStatus(err instanceof Error ? err.message : 'Kunde inte spara');
        }
    };

    return (
        <Formik<TenantFormValues>
            initialValues={toValues(tenant)}
            validationSchema={schema}
            enableReinitialize
            onSubmit={handleSubmit}
        >
            {({ values, errors, touched, status, handleChange, handleBlur, handleSubmit: submitForm, setStatus }) => (
                <Form noValidate onSubmit={submitForm}>
                    <Modal.Body>
                        {status && (
                            <Alert variant="danger" dismissible onClose={() => setStatus(undefined)}>{status}</Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Typ *</Form.Label>
                            <div className="d-flex gap-3">
                                {Object.values(TenantKind).map(kind => (
                                    <Form.Check
                                        key={kind}
                                        type="radio"
                                        id={`tenant-kind-${kind}`}
                                        name="kind"
                                        value={kind}
                                        label={translate(kind)}
                                        checked={values.kind === kind}
                                        onChange={handleChange}
                                    />
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Namn *</Form.Label>
                            <Form.Control
                                name="name"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.name && !!errors.name}
                                autoFocus
                            />
                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                        </Form.Group>

                        {values.kind === TenantKind.Company && (
                            <Form.Group className="mb-3">
                                <Form.Label>Organisationsnummer</Form.Label>
                                <Form.Control
                                    name="orgNumber"
                                    value={values.orgNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="556000-0000"
                                    isInvalid={touched.orgNumber && !!errors.orgNumber}
                                />
                                <Form.Control.Feedback type="invalid">{errors.orgNumber}</Form.Control.Feedback>
                            </Form.Group>
                        )}

                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>E-post</Form.Label>
                                    <Form.Control
                                        name="email"
                                        type="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.email && !!errors.email}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Telefon</Form.Label>
                                    <Form.Control
                                        name="phone"
                                        value={values.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.phone && !!errors.phone}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Gatuadress</Form.Label>
                            <Form.Control
                                name="street"
                                value={values.street}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.street && !!errors.street}
                            />
                            <Form.Control.Feedback type="invalid">{errors.street}</Form.Control.Feedback>
                        </Form.Group>

                        <div className="row">
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Postnummer</Form.Label>
                                    <Form.Control
                                        name="postalCode"
                                        value={values.postalCode}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.postalCode && !!errors.postalCode}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.postalCode}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="col-md-8">
                                <Form.Group className="mb-3">
                                    <Form.Label>Ort</Form.Label>
                                    <Form.Control
                                        name="city"
                                        value={values.city}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        isInvalid={touched.city && !!errors.city}
                                    />
                                    <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-1">
                            <Form.Label>Kommentar</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="comment"
                                value={values.comment}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={touched.comment && !!errors.comment}
                            />
                            <Form.Control.Feedback type="invalid">{errors.comment}</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Text className="text-muted">Inga personnummer. Spara bara det som behövs för avtalet.</Form.Text>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onCancel} disabled={submitting}>Avbryt</Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <><Spinner size="sm" className="me-2" />Sparar...</> : (isEditing ? 'Spara' : 'Skapa')}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}
        </Formik>
    );
};

/** Create/edit a tenant in its own modal. */
const TenantModal: React.FC<TenantModalProps> = ({ show, onHide, onSubmit, tenant, submitting = false }) => {
    const term = useTenantTerm().singular.toLowerCase();
    return (
    <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton>
            <Modal.Title>{tenant ? `Redigera ${term}` : `Ny ${term}`}</Modal.Title>
        </Modal.Header>
        <TenantForm onSubmit={onSubmit} onCancel={onHide} tenant={tenant} submitting={submitting} />
    </Modal>
    );
};

export default TenantModal;
